import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Smartphone, Copy, Check, RefreshCw, Download, Trophy,
  CalendarClock, ListOrdered, ArrowRight, Zap, Info, Globe,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_MODEL = "iphone-17-pro-max";
const SHORTCUT_URL = "https://www.icloud.com/shortcuts/daaec57fc89d4e0f8bbde069eba5d4c9";

// One representative timezone per offset (no need for hundreds of cities).
const TZ_CHOICES = [
  ["Pacific/Honolulu", "Honolulu"],
  ["America/Anchorage", "Alaska"],
  ["America/Los_Angeles", "Los Ángeles / Tijuana"],
  ["America/Denver", "Denver / Chihuahua"],
  ["America/Mexico_City", "Ciudad de México"],
  ["America/Chicago", "Chicago / Centroamérica"],
  ["America/Bogota", "Bogotá / Lima / Quito"],
  ["America/New_York", "Nueva York / Toronto"],
  ["America/Caracas", "Caracas"],
  ["America/Santiago", "Santiago de Chile"],
  ["America/Halifax", "Atlántico (Halifax)"],
  ["America/Sao_Paulo", "São Paulo"],
  ["America/Argentina/Buenos_Aires", "Buenos Aires / Montevideo"],
  ["Atlantic/Cape_Verde", "Cabo Verde"],
  ["UTC", "UTC"],
  ["Europe/London", "Londres / Lisboa"],
  ["Europe/Madrid", "Madrid / París / Berlín / Roma"],
  ["Europe/Athens", "Atenas / El Cairo / Johannesburgo"],
  ["Europe/Moscow", "Moscú / Estambul"],
  ["Asia/Dubai", "Dubái"],
  ["Asia/Tehran", "Teherán"],
  ["Asia/Karachi", "Karachi / Islamabad"],
  ["Asia/Kolkata", "India (Calcuta)"],
  ["Asia/Dhaka", "Daca / Almatý"],
  ["Asia/Bangkok", "Bangkok / Yakarta"],
  ["Asia/Shanghai", "China / Singapur / Hong Kong"],
  ["Asia/Tokyo", "Tokio / Seúl"],
  ["Australia/Sydney", "Sídney"],
  ["Pacific/Auckland", "Auckland"],
];

function tzOffset(id) {
  try {
    const p = new Intl.DateTimeFormat("en-US", {
      timeZone: id, timeZoneName: "shortOffset",
    }).formatToParts(new Date());
    return p.find((x) => x.type === "timeZoneName")?.value || "GMT";
  } catch { return "GMT"; }
}

function offsetMinutes(id) {
  const m = tzOffset(id).replace("GMT", "").match(/([+-]?)(\d{1,2})(?::(\d{2}))?/);
  if (!m) return 0;
  return (m[1] === "-" ? -1 : 1) * ((+m[2]) * 60 + (+(m[3] || 0)));
}

export default function Configurator() {
  const detectedTz = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; }
    catch { return "UTC"; }
  }, []);

  const [models, setModels] = useState([]);
  const [model, setModel] = useState(DEFAULT_MODEL);
  const [tz, setTz] = useState(detectedTz);
  const [meta, setMeta] = useState(null);
  const [copied, setCopied] = useState(false);
  const [bust, setBust] = useState(Date.now());
  const [imgLoading, setImgLoading] = useState(true);

  const tzOptions = useMemo(() => {
    const base = TZ_CHOICES.map(([id, name]) => ({ id, name }));
    if (!base.some((o) => o.id === detectedTz)) {
      base.push({ id: detectedTz, name: detectedTz.split("/").pop().replace(/_/g, " ") });
    }
    return base
      .map((o) => ({ id: o.id, label: `(${tzOffset(o.id)}) ${o.name}`, off: offsetMinutes(o.id) }))
      .sort((a, b) => a.off - b.off);
  }, [detectedTz]);

  useEffect(() => {
    axios.get(`${API}/models`).then((r) => setModels(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/meta?tz=${encodeURIComponent(tz)}`)
      .then((r) => setMeta(r.data))
      .catch(() => {});
  }, [tz]);

  const permanentUrl = useMemo(
    () => `${API}/wallpaper.png?model=${model}&tz=${encodeURIComponent(tz)}`,
    [model, tz]
  );
  const previewUrl = `${permanentUrl}&t=${bust}`;

  const current = models.find((m) => m.key === model);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(permanentUrl);
    } catch {
      const el = document.createElement("textarea");
      el.value = permanentUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    toast.success("Enlace del fondo copiado", {
      description: "Pégalo en tu Atajo de iPhone.",
    });
    setTimeout(() => setCopied(false), 2200);
  };

  const refresh = () => {
    setImgLoading(true);
    setBust(Date.now());
  };

  const aspect = current ? current.width / current.height : 1290 / 2796;

  const steps = [
    { n: "01", t: "Elige tu iPhone y zona horaria", d: "Selecciona arriba tu modelo y tu zona horaria para que la imagen tenga el tamaño y los horarios correctos." },
    { n: "02", t: "Copia el enlace y descarga el atajo", d: "Copia tu enlace permanente y pulsa ‘Descargar atajo’ para añadirlo a tu iPhone." },
    { n: "03", t: "Edita el atajo ‘Wallpaper Mundial’", d: "Abre la app Atajos, edita el atajo ‘Wallpaper Mundial’, pega el enlace en ‘URL’ y selecciona el wallpaper que quieres cambiar (ej. Wallpaper 1)." },
    { n: "04", t: "Crea la automatización diaria", d: "Crea una automatización todos los días a una hora (ej. 5am) que corra el atajo ‘Wallpaper Mundial’." },
  ];

  return (
    <div className="relative z-[2] min-h-screen font-body text-white" data-testid="configurator-page">
      <Toaster position="top-center" theme="dark" />

      {/* Header */}
      <header className="sticky top-0 z-20 border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-accent">
              <Trophy className="h-5 w-5" strokeWidth={2.4} />
            </div>
            <div className="leading-none">
              <p className="font-display text-xl font-bold tracking-wide">MUNDIAL DIARIO</p>
              <p className="text-[11px] uppercase tracking-[0.25em] text-white/40">Generador de fondos</p>
            </div>
          </div>
          <a
            href={previewUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="open-image-link"
            className="hidden items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white sm:flex"
          >
            <Download className="h-4 w-4" /> Abrir imagen
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10 md:px-10 md:py-16">
        {/* Hero copy */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mb-10 max-w-3xl"
        >
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-brand-accent/30 bg-brand-accent/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-brand-accent">
            <Zap className="h-3.5 w-3.5" /> Copa Mundial FIFA {meta?.season || 2026}
          </div>
          <h1 className="font-display text-5xl font-black leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl">
            UN ENLACE.<br />
            <span className="text-brand-accent">CLASIFICACIÓN NUEVA</span> CADA DÍA.
          </h1>
          <p className="mt-5 max-w-xl text-base text-white/55 sm:text-lg">
            Un fondo de pantalla que se actualiza solo con la clasificación de grupos en
            vivo, los resultados de ayer y los partidos de hoy — con el tamaño perfecto para
            tu iPhone. Configúralo una vez con un Atajo y despierta con lo último cada mañana.
          </p>
        </motion.div>

        {meta?.demo_mode && (
          <div
            data-testid="demo-banner"
            className="mb-8 flex items-start gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-5 py-4 text-sm text-amber-200"
          >
            <Info className="mt-0.5 h-5 w-5 shrink-0" />
            <p>
              <span className="font-semibold">Vista previa con datos del torneo {meta.real_season}.</span>{" "}
              Tu plan gratuito aún no accede a la temporada {meta.season} — en cuanto esté disponible (o mejores la clave),
              el fondo cambiará a datos en tiempo real de {meta.season} automáticamente. El enlace no cambia.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          {/* LEFT: controls */}
          <div className="flex flex-col gap-7 lg:col-span-5">
            {/* model select */}
            <div className="rounded-3xl border border-brand-border bg-brand-surface p-6">
              <label className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-white/50">
                <Smartphone className="h-4 w-4" /> Tu modelo de iPhone
              </label>
              <Select value={model} onValueChange={(v) => { setModel(v); setImgLoading(true); }}>
                <SelectTrigger
                  data-testid="model-select"
                  className="h-14 rounded-xl border-brand-border bg-brand-elevated px-4 text-base font-semibold text-white focus:ring-brand-accent"
                >
                  <SelectValue placeholder="Selecciona tu iPhone" />
                </SelectTrigger>
                <SelectContent className="max-h-80 border border-white/10 bg-[#1A1C22] text-white shadow-2xl shadow-black/70">
                  {models.map((m) => (
                    <SelectItem
                      key={m.key}
                      value={m.key}
                      data-testid={`model-option-${m.key}`}
                      className="cursor-pointer focus:bg-white/10 focus:text-white data-[state=checked]:text-brand-accent"
                    >
                      {m.label} · {m.width}×{m.height}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {current && (
                <p className="mt-3 font-mono2 text-xs text-white/40">
                  Resolución de salida {current.width} × {current.height}px
                </p>
              )}
            </div>

            {/* timezone */}
            <div className="rounded-3xl border border-brand-border bg-brand-surface p-6">
              <label className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-white/50">
                <Globe className="h-4 w-4" /> Tu zona horaria
              </label>
              <Select value={tz} onValueChange={(v) => { setTz(v); setImgLoading(true); }}>
                <SelectTrigger
                  data-testid="timezone-select"
                  className="h-14 rounded-xl border-brand-border bg-brand-elevated px-4 text-base font-semibold text-white focus:ring-brand-accent"
                >
                  <SelectValue placeholder="Selecciona tu ubicación" />
                </SelectTrigger>
                <SelectContent className="max-h-80 border border-white/10 bg-[#1A1C22] text-white shadow-2xl shadow-black/70">
                  {tzOptions.map((z) => (
                    <SelectItem
                      key={z.id}
                      value={z.id}
                      data-testid={`tz-option-${z.id}`}
                      className="cursor-pointer focus:bg-white/10 focus:text-white data-[state=checked]:text-brand-accent"
                    >
                      {z.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-3 text-xs leading-relaxed text-white/40">
                Las horas de los partidos y el cambio de día se ajustan a esta zona.
                La detectamos automáticamente, pero puedes cambiarla.
              </p>
            </div>

            {/* permanent link */}
            <div className="rounded-3xl border border-brand-border bg-brand-surface p-6">
              <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-white/50">
                Tu enlace permanente del fondo
              </p>
              <div
                data-testid="permanent-url"
                className="mb-4 break-all rounded-xl border border-brand-border bg-black/40 px-4 py-3 font-mono2 text-xs text-brand-accent"
              >
                {permanentUrl}
              </div>
              <button
                onClick={copyUrl}
                data-testid="copy-button"
                className="flex h-14 w-full items-center justify-center gap-2 rounded-xl border-2 border-brand-accent bg-transparent text-base font-bold uppercase tracking-wider text-brand-accent transition-colors hover:bg-brand-accent/10"
              >
                {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                {copied ? "¡Copiado!" : "Copiar enlace"}
              </button>
              <a
                href={SHORTCUT_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="download-shortcut-button"
                className="mt-3 flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-brand-accent text-base font-bold uppercase tracking-wider text-[#101A23] shadow-lg shadow-brand-accent/30 transition-colors hover:bg-[#cc6d00]"
              >
                <Download className="h-5 w-5" /> Descargar atajo
              </a>
              <p className="mt-3 text-xs leading-relaxed text-white/40">
                Este enlace nunca cambia — siempre devuelve el fondo de hoy. Perfecto para una
                automatización con Atajos de iPhone.
              </p>
            </div>

            {/* stats */}
            <div className="grid grid-cols-3 gap-3" data-testid="stats-row">
              <Stat icon={CalendarClock} label="Hoy" value={meta?.today_match_count ?? "–"} sub="partidos" />
              <Stat icon={ListOrdered} label="Ayer" value={meta?.yesterday_result_count ?? "–"} sub="resultados" />
              <Stat icon={Trophy} label="Grupos" value={meta?.group_count ?? "–"} sub="tablas" />
            </div>
          </div>

          {/* RIGHT: preview */}
          <div className="lg:col-span-7">
            <div className="relative flex flex-col items-center rounded-3xl border border-brand-border bg-brand-surface p-6 md:p-10">
              <div className="mb-6 flex w-full items-center justify-between">
                <p className="font-display text-2xl font-bold tracking-wide">VISTA PREVIA</p>
                <button
                  onClick={refresh}
                  data-testid="refresh-button"
                  className="flex items-center gap-2 rounded-full border border-brand-border bg-brand-elevated px-4 py-2 text-sm text-white/70 transition-colors hover:text-white"
                >
                  <RefreshCw className={`h-4 w-4 ${imgLoading ? "animate-spin" : ""}`} /> Actualizar
                </button>
              </div>

              {/* iPhone frame */}
              <div
                className="relative rounded-[2.5rem] border-[10px] border-black bg-black shadow-2xl"
                style={{ width: `${aspect * 600}px`, maxWidth: "100%" }}
                data-testid="wallpaper-preview"
              >
                <div className="absolute left-1/2 top-2 z-10 h-6 w-24 -translate-x-1/2 rounded-full bg-black" />
                <div className="overflow-hidden rounded-[2rem]" style={{ aspectRatio: aspect }}>
                  {imgLoading && (
                    <div className="absolute inset-0 z-0 flex items-center justify-center bg-brand-bg">
                      <RefreshCw className="h-8 w-8 animate-spin text-white/30" />
                    </div>
                  )}
                  <img
                    key={previewUrl}
                    src={previewUrl}
                    alt="Vista previa del fondo del Mundial"
                    className="h-full w-full object-cover"
                    onLoad={() => setImgLoading(false)}
                    onError={() => setImgLoading(false)}
                  />
                </div>
              </div>
              <p className="mt-6 text-center text-sm text-white/40">
                Mostrando <span className="text-white/70">{current?.label || "iPhone"}</span> ·
                se actualiza a diario en el mismo enlace
              </p>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <section className="mt-20" data-testid="instructions-section">
          <div className="mb-8 flex items-center gap-3">
            <div className="h-8 w-1.5 rounded-full bg-brand-accent" />
            <h2 className="font-display text-3xl font-bold tracking-wide md:text-4xl">
              CONFIGURA LA AUTOMATIZACIÓN EN iPHONE
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-3xl border border-brand-border bg-brand-border sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="flex flex-col gap-3 bg-brand-surface p-6"
              >
                <span className="font-mono2 text-sm text-brand-accent">{s.n}</span>
                <h3 className="font-display text-xl font-bold tracking-wide">{s.t}</h3>
                <p className="text-sm leading-relaxed text-white/50">{s.d}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-2 rounded-2xl border border-brand-border bg-brand-surface px-5 py-4 text-sm text-white/55">
            <ArrowRight className="h-4 w-4 text-brand-accent" />
            Consejo: al crear la automatización, desactiva “Preguntar antes de ejecutar” para que el fondo se actualice solo cada mañana, sin confirmaciones.
          </div>
        </section>

        <footer className="mt-20 border-t border-brand-border pt-8 text-center text-sm text-white/30">
          Hecho para la Copa Mundial FIFA {meta?.season || 2026}. Datos de worldcup26.ir.
        </footer>
      </main>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-brand-border bg-brand-surface p-4">
      <Icon className="mb-2 h-4 w-4 text-brand-accent" />
      <p className="font-display text-3xl font-bold leading-none">{value}</p>
      <p className="mt-1 text-[11px] uppercase tracking-wider text-white/40">{label} · {sub}</p>
    </div>
  );
}
